import { useState } from 'react'

import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { useAppToast } from '../../../helpers'
import { RequestNoteService } from '../../../services/Request/RequestNoteService'

export interface IRequestNoteDeleteDialogProps {
  isOpen: boolean
  onClose: () => void
  request: { id: number } | undefined
  onDelete: () => void
}

const genericError = 'Erro ao deletar observação, tente novamente mais tarde.'

/**
 * Mantido como componente porque a exclusão da observação tem regra própria: a API
 * responde 200 com `success: false`, e o middleware de permissão responde em inglês.
 */
export function RequestNoteDeleteDialog({ isOpen, onClose, request, onDelete }: IRequestNoteDeleteDialogProps) {
  const requestNoteService = new RequestNoteService()
  const [isLoading, setIsLoading] = useState(false)
  const toast = useAppToast()

  const showError = (description: string) => {
    toast.error('Erro ao excluir observação', description)
  }

  const handleDelete = async () => {
    if (!request) {
      return
    }

    setIsLoading(true)
    try {
      const response = await requestNoteService.destroy(request.id)

      // The API may answer 200 with `success: false`, so a resolved promise alone
      // is not enough to claim the note was deleted.
      if (response?.success === false) {
        showError(response?.message ?? genericError)
        return
      }

      toast.success('Observação excluída')

      onDelete()
      onClose()
    } catch (error) {
      const data = (error as { response?: { data?: { reason?: string; message?: string } } })?.response?.data

      // The role middleware answers a plain English "Forbidden" — translate it.
      if (data?.reason === 'permission-denied') {
        showError('Você não tem permissão para excluir esta observação.')
        return
      }

      showError(data?.message ?? genericError)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleDelete}
      action="delete"
      entity="observação"
      feminine
      isLoading={isLoading}
    />
  )
}
